import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Clock, XCircle, Loader2, ArrowLeft } from "lucide-react";
import { useLang } from "@/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 6;

export default function PaymentResult({ cancelled = false }) {
  const { t } = useLang();
  const p = t.payment;
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancel" : "checking");
  const [interval, setInterval_] = useState("once");

  useEffect(() => {
    if (cancelled || !sessionId) {
      if (!cancelled) setState("fail");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/donations/status/${sessionId}`);
        setInterval_(data.interval || "once");
        if (data.payment_status === "paid") return setState("success");
        if (["expired", "failed"].includes(data.payment_status)) return setState("fail");
      } catch {
        return setState("fail");
      }
      attempts += 1;
      if (attempts >= MAX_POLLS) return setState("pending");
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, cancelled]);

  const views = {
    checking: { icon: <Loader2 size={44} className="animate-spin text-amber-400" />, title: p.checking, desc: "" },
    success: {
      icon: <CheckCircle2 size={44} className="text-amber-400" />,
      title: p.successTitle,
      desc: interval === "month" ? `${p.successMonthly} ${p.successDesc}` : p.successDesc,
    },
    pending: { icon: <Clock size={44} className="text-amber-400" />, title: p.pendingTitle, desc: p.pendingDesc },
    fail: { icon: <XCircle size={44} className="text-red-400" />, title: p.failTitle, desc: p.failDesc },
    cancel: { icon: <XCircle size={44} className="text-slate-400" />, title: p.cancelTitle, desc: p.cancelDesc },
  };
  const v = views[state];

  return (
    <div
      data-testid={`payment-result-${state}`}
      className="flex min-h-screen items-center justify-center bg-[#0A0C10] px-6 text-slate-100"
    >
      <div className="bg-noise" aria-hidden="true" />
      <div className="relative w-full max-w-xl border border-white/10 bg-[#0E1119] p-10 text-center sm:p-14">
        <div className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-amber-400/10 blur-3xl" />
        <div className="relative">
          <span className="font-code text-[11px] uppercase tracking-[0.35em] text-amber-400">
            Complejo Montalbán
          </span>
          <div className="mt-8 flex justify-center">{v.icon}</div>
          <h1
            data-testid="payment-result-title"
            className="mt-6 font-display text-3xl font-bold uppercase leading-tight text-white sm:text-4xl"
          >
            {v.title}
          </h1>
          {v.desc && (
            <p className="mt-5 text-sm leading-relaxed text-slate-300 sm:text-base">{v.desc}</p>
          )}
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/"
              data-testid="payment-back-link"
              className="inline-flex items-center justify-center gap-2 bg-amber-400 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300"
            >
              <ArrowLeft size={15} /> {p.back}
            </Link>
            {(state === "fail" || state === "cancel") && (
              <Link
                to="/#contribuir"
                data-testid="payment-retry-link"
                className="inline-flex items-center justify-center border border-white/20 px-6 py-3.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:border-amber-400 hover:text-amber-400"
              >
                {p.retry}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
